import React, { useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import {
  Alert,
  Button,
  Descriptions,
  Empty,
  Image,
  Input,
  Modal,
  Spin,
  Tag,
  message,
} from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import CmsTopBar from "components/cmslayout/CmsTopBar";
import martEmptyImg from "assets/images/mart-empty.png";
import type { MartFormValues } from "./types";
import type { MarketPayload } from "types/market";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  clearMarketError,
  clearSelectedMarket,
  createMarket,
  deleteMarket,
  fetchMarketById,
  fetchMarkets,
  updateMarket,
  uploadMarketImage,
} from "store/slices/marketSlice";
import MartFormModal from "./components/MartFormModal";
import MartCard from "./components/MartCard";
import DeleteMartModal from "./components/DeleteMartModal";

interface MarketsPageProps {}

export const MarketsPage: React.FunctionComponent<MarketsPageProps> = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    actionLoading,
    detailLoading,
    error,
    loading,
    markets,
    selectedMarket,
    uploadLoading,
  } = useAppSelector((state) => state.markets);
  const [addEditOpen, setAddEditOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [search, setSearch] = useState("");

  const mode: "add" | "edit" = editingId ? "edit" : "add";
  const modalLoading = actionLoading || uploadLoading || detailLoading;
  const editingMart = useMemo(
    () =>
      selectedMarket?.id === editingId
        ? selectedMarket
        : markets.find((market) => market.id === editingId) ?? null,
    [markets, editingId, selectedMarket]
  );

  useEffect(() => {
    dispatch(fetchMarkets());
  }, [dispatch]);

  const openAdd = () => {
    dispatch(clearMarketError());
    setEditingId(null);
    setAddEditOpen(true);
  };

  const openEdit = (id: number) => {
    dispatch(clearMarketError());
    setEditingId(id);
    setAddEditOpen(true);
    dispatch(fetchMarketById(id));
  };

  const closeModal = () => {
    setAddEditOpen(false);
    setEditingId(null);
    dispatch(clearSelectedMarket());
  };

  const openView = (id: number) => {
    dispatch(clearMarketError());
    setDetailOpen(true);
    dispatch(fetchMarketById(id));
  };

  const closeDetail = () => {
    setDetailOpen(false);
    dispatch(clearSelectedMarket());
  };

  const requestDelete = (id: number) => {
    dispatch(clearMarketError());
    setDeletingId(id);
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    setDeleteOpen(false);
    setDeletingId(null);
  };

  const confirmDelete = async () => {
    if (!deletingId) return;

    const name = markets.find((market) => market.id === deletingId)?.name;
    const result = await dispatch(deleteMarket(deletingId));

    if (deleteMarket.fulfilled.match(result)) {
      if (editingId === deletingId) closeModal();
      closeDelete();
      message.success(`Market${name ? ` (${name})` : ""} deleted.`);
      return;
    }

    message.error(result.payload || "Unable to delete market.");
  };

  const getSelectedLogoFile = (values: MartFormValues) => {
    const imageList = values.image || [];
    return imageList[0]?.originFileObj as File | undefined;
  };

  const buildTrimmedPayload = (
    values: MartFormValues,
    logo: string
  ): MarketPayload => ({
    logo,
    name: values.name.trim(),
    location: values.location.trim(),
    city: (values.city || "").trim(),
    state: (values.state || "").trim(),
    lat: Number(values.lat),
    lng: Number(values.lng),
    description: values.description.trim(),
    status: values.status === "upcoming" ? "upcoming" : "active",
  });

  const handleSubmit = async (values: MartFormValues) => {
    const hasCoordinates =
      values.lat !== null &&
      values.lat !== undefined &&
      Number.isFinite(Number(values.lat)) &&
      values.lng !== null &&
      values.lng !== undefined &&
      Number.isFinite(Number(values.lng));

    if (!hasCoordinates) {
      message.error("Please select market location from map");
      return;
    }

    if (!values.city?.trim() || !values.state?.trim()) {
      message.error("Please provide city and state for market location");
      return;
    }

    const selectedFile = getSelectedLogoFile(values);
    let logo = editingMart?.logo || "";

    if (selectedFile) {
      const uploadResult = await dispatch(uploadMarketImage(selectedFile));

      if (!uploadMarketImage.fulfilled.match(uploadResult)) {
        message.error(
          uploadResult.payload || "Image upload failed. Please try again."
        );
        return;
      }

      logo = uploadResult.payload;
    }

    if (!logo) {
      message.error("Image upload failed. Please try again.");
      return;
    }

    const payload = buildTrimmedPayload(values, logo);

    if (editingId && editingMart) {
      const updateResult = await dispatch(updateMarket({ id: editingId, payload }));

      if (!updateMarket.fulfilled.match(updateResult)) {
        message.error(updateResult.payload || "Unable to update market.");
        return;
      }

      closeModal();
      message.success("Market updated.");
      return;
    }

    const createResult = await dispatch(createMarket(payload));

    if (!createMarket.fulfilled.match(createResult)) {
      message.error(createResult.payload || "Unable to create market.");
      return;
    }

    closeModal();
    message.success("Market added.");
  };

  const filteredMarkets = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return markets;

    return markets.filter(
      (market) =>
        market.name.toLowerCase().includes(q) ||
        market.location.toLowerCase().includes(q) ||
        market.description.toLowerCase().includes(q)
    );
  }, [markets, search]);

  const viewStores = (id: number) => {
    navigate(`/markets/${id}/stores`);
  };

  const formatDate = (value?: string) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
  };

  return (
    <React.Fragment>
      <CmsTopBar title="Markets" />

      <Container fluid className="dashboard-wrap">
        <Container fluid className="px-3 py-4">
          <div className="markets-section-header mb-3">
            <h2 className="markets-heading">Markets ({markets.length})</h2>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              className="markets-add-btn"
              onClick={openAdd}
            >
              Add New Marts
            </Button>
          </div>

          <Row className="mb-3">
            <Col span={24}>
              <Input
                placeholder="Search"
                prefix={<SearchOutlined className="markets-search-icon" />}
                className="markets-search"
                allowClear
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </Col>
          </Row>

          {error && (
            <Alert
              type="error"
              message={error}
              showIcon
              closable
              className="mb-3"
              onClose={() => dispatch(clearMarketError())}
            />
          )}

          <div className="markets-empty-wrap">
            {loading ? (
              <Spin size="large" />
            ) : filteredMarkets.length === 0 ? (
              <Empty
                image={
                  <div
                    style={{
                      height: 130,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={martEmptyImg}
                      alt="No Mart Added"
                      style={{
                        width: 120,
                        height: 120,
                        objectFit: "contain",
                        display: "block",
                      }}
                    />
                  </div>
                }
                description={
                  <div style={{ textAlign: "center", marginTop: 12 }}>
                    <div
                      style={{
                        fontSize: 20,
                        fontWeight: 600,
                        color: "#1f1f1f",
                      }}
                    >
                      No Mart Added
                    </div>
                    <div
                      style={{
                        fontSize: 14,
                        color: "#8c8c8c",
                        marginTop: 4,
                      }}
                    >
                      No markets found.
                    </div>
                  </div>
                }
              />
            ) : (
              <div className="markets-card-grid">
                {filteredMarkets.map((mart) => (
                  <MartCard
                    key={mart.id}
                    mart={mart}
                    onView={openView}
                    onEdit={openEdit}
                    onDeleteRequest={requestDelete}
                    onViewStores={viewStores}
                  />
                ))}
              </div>
            )}
          </div>
        </Container>
      </Container>

      <MartFormModal
        open={addEditOpen}
        mode={mode}
        initialMart={editingMart}
        loading={modalLoading}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />

      <DeleteMartModal
        open={deleteOpen}
        martName={markets.find((market) => market.id === deletingId)?.name}
        loading={actionLoading}
        onClose={closeDelete}
        onConfirm={confirmDelete}
      />

      <Modal
        title="Market Details"
        open={detailOpen}
        onCancel={closeDetail}
        footer={null}
        centered
        width={620}
        destroyOnClose
      >
        {detailLoading ? (
          <div className="market-detail-loading">
            <Spin />
          </div>
        ) : selectedMarket ? (
          <div className="market-detail">
            <Image
              src={selectedMarket.logo || martEmptyImg}
              fallback={martEmptyImg}
              alt={selectedMarket.name}
              className="market-detail-logo"
              preview={false}
            />
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Name">
                {selectedMarket.name}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={selectedMarket.status === "upcoming" ? "warning" : "success"}>
                  {selectedMarket.status === "upcoming" ? "Upcoming" : "Active"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Location">
                {selectedMarket.location}
              </Descriptions.Item>
              <Descriptions.Item label="Description">
                {selectedMarket.description}
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {formatDate(selectedMarket.created_at)}
              </Descriptions.Item>
              <Descriptions.Item label="Updated At">
                {formatDate(selectedMarket.updated_at)}
              </Descriptions.Item>
            </Descriptions>
          </div>
        ) : (
          <Empty description="No market found." />
        )}
      </Modal>
    </React.Fragment>
  );
};

export default MarketsPage;
